import React, { createContext, useContext, useState, useCallback } from "react";
import { UserStoryDto } from "../../dto/UserStoryDto";
import { api } from "../../../../interceptors/api";

interface UserStoriesContextType {
  stories: UserStoryDto[];
  loading: boolean;
  loadStories: () => Promise<void>;
  refreshStoriesWithVisuals: () => Promise<void>;
}

const UserStoriesContext = createContext<UserStoriesContextType | undefined>(
  undefined
);

export const useUserStories = () => {
  const context = useContext(UserStoriesContext);
  if (!context) {
    throw new Error("useUserStories must be used within UserStoriesProvider");
  }
  return context;
};

interface UserStoriesProviderProps {
  children: React.ReactNode;
}

const PLACEHOLDER_ICON = "/assets/icons/downloading-file-animated.png";
const PLACEHOLDER_TITLE = "Geschichte";

export const UserStoriesProvider: React.FC<UserStoriesProviderProps> = ({
  children,
}) => {
  const [stories, setStories] = useState<UserStoryDto[]>([]);
  const [loading, setLoading] = useState(false);

  // Initial load - fetches all stories
  const loadStories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/user-story/device/4");
      const loadedStories = res.data.map((story: UserStoryDto) => ({
        ...story,
        // Use placeholders if visual or titles are empty or null
        visualUrl: story.visualUrl || PLACEHOLDER_ICON,
        titleDE: story.titleDE || PLACEHOLDER_TITLE,
        titleEN: story.titleEN || PLACEHOLDER_TITLE,
      }));
      setStories(loadedStories);
    } catch (err) {
      console.error("Failed to load user stories:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh mechanism - updates stories that now have visuals or titles
  // Fetches only stories without visuals/titles and pre-loads images before updating state
  const refreshStoriesWithVisuals = useCallback(async () => {
    try {
      // Get stories that currently have placeholder icons or titles
      const storiesWithoutVisualsOrTitles = stories.filter(
        (s) =>
          s.visualUrl === PLACEHOLDER_ICON ||
          s.titleDE === PLACEHOLDER_TITLE ||
          s.titleEN === PLACEHOLDER_TITLE
      );

      if (storiesWithoutVisualsOrTitles.length === 0) {
        // All stories already have visuals and titles
        return;
      }

      const ids = storiesWithoutVisualsOrTitles.map((s) => s.id).join(",");

      // Fetch only stories by their IDs using the new endpoint
      const res = await api.get("/api/user-story/by-ids", {
        params: { ids },
      });
      const updatedStories = res.data;

      // Filter stories that now have visuals or titles
      const storiesWithNewContent = updatedStories.filter(
        (story: UserStoryDto) =>
          (story.visualUrl && story.visualUrl !== "") ||
          (story.titleDE && story.titleDE !== "") ||
          (story.titleEN && story.titleEN !== "")
      );

      if (storiesWithNewContent.length === 0) {
        // No new visuals or titles generated yet
        return;
      }

      // Pre-load images for stories with new visuals before updating state
      // This prevents the flash of broken images or loading delays in the UI
      const storiesWithNewVisuals = storiesWithNewContent.filter(
        (story: UserStoryDto) => story.visualUrl && story.visualUrl !== ""
      );

      const imagePromises = storiesWithNewVisuals.map(
        (story: UserStoryDto) => {
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Continue even if image fails to load
            img.src = story.visualUrl!;
          });
        }
      );

      // Wait for all images to load (or fail) - images are now in browser cache
      await Promise.all(imagePromises);

      // Now update state - images are already cached, so rendering will be instant
      setStories((prevStories) =>
        prevStories.map((story) => {
          // Find the corresponding updated story
          const updatedStory = updatedStories.find(
            (us: UserStoryDto) => us.id === story.id
          );

          if (!updatedStory) return story;

          // Check if any updates are needed
          let hasUpdates = false;
          const updates: Partial<UserStoryDto> = {};

          // If story now has a real visual, update it
          if (
            story.visualUrl === PLACEHOLDER_ICON &&
            updatedStory.visualUrl &&
            updatedStory.visualUrl !== ""
          ) {
            updates.visualUrl = updatedStory.visualUrl;
            hasUpdates = true;
          }

          // If story now has a real title (DE), update it
          if (
            story.titleDE === PLACEHOLDER_TITLE &&
            updatedStory.titleDE &&
            updatedStory.titleDE !== ""
          ) {
            updates.titleDE = updatedStory.titleDE;
            hasUpdates = true;
          }

          // If story now has a real title (EN), update it
          if (
            story.titleEN === PLACEHOLDER_TITLE &&
            updatedStory.titleEN &&
            updatedStory.titleEN !== ""
          ) {
            updates.titleEN = updatedStory.titleEN;
            hasUpdates = true;
          }

          return hasUpdates ? { ...story, ...updates } : story;
        })
      );

      console.log(
        `Pre-loaded and updated ${storiesWithNewVisuals.length} visual(s) and ${storiesWithNewContent.length} story content update(s)`
      );
    } catch (err) {
      console.error("Failed to refresh user story visuals:", err);
    }
  }, [stories]);

  const value: UserStoriesContextType = {
    stories,
    loading,
    loadStories,
    refreshStoriesWithVisuals,
  };

  return (
    <UserStoriesContext.Provider value={value}>
      {children}
    </UserStoriesContext.Provider>
  );
};
